import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Clock, Mail, Lock, AlertCircle, Building2, ArrowLeft, Eye, EyeOff, Crown } from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [appName, setAppName] = useState('Regulisto');
  
  // Multi-tenant state
  const [step, setStep] = useState('credentials'); // 'credentials' | 'organization'
  const [tempToken, setTempToken] = useState(null);
  const [tempUser, setTempUser] = useState(null);
  const [availableOrgs, setAvailableOrgs] = useState([]);
  
  const { login, selectOrganization, continueWithoutOrg } = useAuth();
  const { t, language, toggleLanguage } = useLanguage();
  const navigate = useNavigate();

  // Fetch public settings for app branding
  useEffect(() => {
    const fetchAppName = async () => {
      try {
        const response = await axios.get(`${API_URL}/settings/public`);
        if (response.data?.app_name) {
          setAppName(response.data.app_name);
        }
      } catch (error) {
        console.error('Error fetching public settings:', error);
      }
    };
    fetchAppName();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    const result = await login(email, password);
    
    if (result.success) {
      if (result.requiresOrgSelection) {
        // Multiple organizations - show selector
        setTempToken(result.tempToken);
        setTempUser(result.user);
        setAvailableOrgs(result.organizations);
        setStep('organization');
      } else {
        navigate('/dashboard');
      }
    } else {
      setError(result.error);
    }
    
    setLoading(false);
  };

  const handleSelectOrganization = async (orgId) => {
    setError('');
    setLoading(true);

    const result = await selectOrganization(orgId, tempToken);
    
    if (result.success) {
      navigate('/dashboard');
    } else {
      setError(result.error);
    }
    
    setLoading(false);
  };

  const handleBackToCredentials = () => {
    setStep('credentials');
    setTempToken(null);
    setTempUser(null);
    setAvailableOrgs([]);
    setError('');
  };

  const handleContinueWithoutOrg = () => {
    // For superadmins to skip org selection and go directly to admin panel
    const result = continueWithoutOrg(tempToken, tempUser);
    if (result.success) {
      navigate('/organizations');
    }
  };

  return (
    <div className="min-h-screen flex" data-testid="login-page">
      {/* Left side - Image */}
      <div 
        className="hidden lg:flex lg:w-1/2 bg-cover bg-center relative"
        style={{
          backgroundImage: `url('https://images.unsplash.com/photo-1600447634369-e6bf93c83435?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3MjQyMTd8MHwxfHNlYXJjaHwzfHxtb2Rlcm4lMjBtaW5pbWFsaXN0JTIwb2ZmaWNlJTIwYnJpZ2h0JTIwc3VubGlnaHR8ZW58MHx8fHx3aGl0ZXwxNzY2MzM1NDAzfDA&ixlib=rb-4.1.0&q=85')`
        }}
      >
        <div className="absolute inset-0 bg-indigo-900/20 backdrop-blur-[2px]" />
        <div className="relative z-10 flex flex-col justify-end p-12 text-white">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 bg-white/20 backdrop-blur-md rounded-xl flex items-center justify-center">
              <Clock className="w-6 h-6" />
            </div>
            <span className="text-2xl font-bold font-['Manrope']">{appName}</span>
          </div>
          <p className="text-lg text-white/90 max-w-md">
            {language === 'es' 
              ? 'Control horario inteligente que cumple con la normativa española. Simple, seguro y eficiente.'
              : 'Smart time tracking that complies with Spanish regulations. Simple, secure and efficient.'}
          </p>
        </div>
      </div>

      {/* Right side - Login Form */}
      <div className="flex-1 flex flex-col justify-center items-center p-8 bg-slate-50">
        <div className="absolute top-4 right-4">
          <Button 
            variant="ghost" 
            onClick={toggleLanguage}
            className="text-slate-600"
            data-testid="language-toggle"
          >
            {language === 'es' ? '🇬🇧 EN' : '🇪🇸 ES'}
          </Button>
        </div>

        <Card className="w-full max-w-md shadow-lg border-slate-200">
          <CardHeader className="text-center pb-2">
            <div className="lg:hidden flex items-center justify-center gap-2 mb-4">
              <div className="w-10 h-10 bg-indigo-600 rounded-xl flex items-center justify-center">
                <Clock className="w-5 h-5 text-white" />
              </div>
              <span className="text-xl font-bold font-['Manrope'] text-slate-900">{appName}</span>
            </div>
            
            {step === 'credentials' ? (
              <>
                <CardTitle className="text-2xl font-['Manrope'] text-slate-900">
                  {t('welcome_back')}
                </CardTitle>
                <CardDescription className="text-slate-500">
                  {t('sign_in_to_continue')}
                </CardDescription>
              </>
            ) : (
              <>
                <CardTitle className="text-2xl font-['Manrope'] text-slate-900">
                  {language === 'es' ? 'Seleccionar Empresa' : 'Select Organization'}
                </CardTitle>
                <CardDescription className="text-slate-500">
                  {language === 'es' 
                    ? `Hola ${tempUser?.name}, selecciona la empresa a la que deseas acceder`
                    : `Hello ${tempUser?.name}, select the organization you want to access`}
                </CardDescription>
              </>
            )}
          </CardHeader>
          <CardContent>
            {step === 'credentials' ? (
              <form onSubmit={handleSubmit} className="space-y-4">
                {error && (
                  <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-md text-red-700 text-sm" data-testid="login-error">
                    <AlertCircle className="w-4 h-4" />
                    {error}
                  </div>
                )}
                
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-slate-700">{t('email')}</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      id="email"
                      type="email"
                      placeholder="nombre@empresa.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="pl-10"
                      required
                      data-testid="email-input"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="password" className="text-slate-700">{t('password')}</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      id="password"
                      type={showPassword ? "text" : "password"}
                      placeholder="••••••••"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="pl-10 pr-10"
                      required
                      data-testid="password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                      data-testid="toggle-password"
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                <div className="flex justify-end">
                  <Link 
                    to="/forgot-password" 
                    className="text-sm text-indigo-600 hover:text-indigo-700 hover:underline"
                    data-testid="forgot-password-link"
                  >
                    {language === 'es' ? '¿Olvidaste tu contraseña?' : 'Forgot your password?'}
                  </Link>
                </div>

                <Button 
                  type="submit" 
                  className="w-full bg-indigo-600 hover:bg-indigo-700"
                  disabled={loading}
                  data-testid="login-submit"
                >
                  {loading ? (
                    <span className="flex items-center gap-2">
                      <span className="spinner" />
                      {t('loading')}
                    </span>
                  ) : (
                    t('login')
                  )}
                </Button>
              </form>
            ) : (
              <div className="space-y-4">
                {error && (
                  <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-md text-red-700 text-sm">
                    <AlertCircle className="w-4 h-4" />
                    {error}
                  </div>
                )}
                
                <div className="space-y-2">
                  {availableOrgs.map((org) => (
                    <button
                      key={org.id}
                      onClick={() => handleSelectOrganization(org.id)}
                      disabled={loading}
                      className="w-full flex items-center gap-3 p-4 rounded-lg border border-slate-200 hover:border-indigo-300 hover:bg-indigo-50 transition-colors text-left"
                      data-testid={`org-select-${org.id}`}
                    >
                      <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center">
                        <Building2 className="w-5 h-5 text-indigo-600" />
                      </div>
                      <div>
                        <div className="font-medium text-slate-900">{org.name}</div>
                        {org.plan?.display_name && (
                          <div className="text-sm text-slate-500">{org.plan.display_name}</div>
                        )}
                      </div>
                    </button>
                  ))}
                </div>

                {/* Superadmin option to skip org selection */}
                {tempUser?.role === 'superadmin' && (
                  <Button
                    variant="outline"
                    onClick={handleContinueWithoutOrg}
                    className="w-full border-amber-300 text-amber-700 hover:bg-amber-50"
                    disabled={loading}
                    data-testid="continue-without-org-btn"
                  >
                    <Crown className="w-4 h-4 mr-2" />
                    {language === 'es' ? 'Continuar al Panel de Admin' : 'Continue to Admin Panel'}
                  </Button>
                )}

                <Button
                  variant="ghost"
                  onClick={handleBackToCredentials}
                  className="w-full text-slate-600"
                  disabled={loading}
                >
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  {language === 'es' ? 'Volver' : 'Back'}
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Login;
